import { EstateType } from "@1games-ru/region-shared/dist/enums/estate/estate";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import formatPrice from "@/core/helpers/formatPrice";
import useAppSelector from "@/core/helpers/useAppSelector";
import { House3Icon } from "@/shared/icons";
import { Button, Modal } from "@/shared/ui";
import OfferCheckbox from "@/shared/widgets/OfferCheckbox";
import OfferPlayerInfo from "@/shared/widgets/OfferPlayerInfo";
import OfferView from "@/shared/widgets/OfferView";
import { offerResolverSelector } from "@/domains/offer/slice";
import getEstateImage from "@/domains/estate/helpers/getEstateImage";
import useOfferResolver from "@/domains/offer/hooks/useOfferResolver";
import useOfferResolverEstate from "@/domains/offer/hooks/useOfferResolverEstate";
import OfferEstateInfo from "@/domains/offer/components/estate/OfferEstateInfo";
import OfferEstateLocation from "@/domains/offer/components/estate/OfferEstateLocation";

export default function OfferEstateTenantResolverContainer({ isShow }: { isShow: boolean }) {
  const { t } = useTranslation("offer");

  const [isAgree, setIsAgree] = useState(false);

  const offerResolver = useAppSelector(offerResolverSelector);

  const { pending, estate } = useOfferResolverEstate(isShow);
  const { pending: isLoading, resolveAccept, reject } = useOfferResolver();

  const level = estate ? t(`estate:estate.level.${estate.level}`) : "";

  const complexId =
    estate?.entity && estate.entity.type === EstateType.Apartment
      ? estate?.entity?.content?.complexId
      : null;

  const src = estate
    ? getEstateImage({ id: estate?.id, type: estate?.entity?.type, complexId }, "medium")
    : "";

  const flatNumber =
    estate?.entity && estate.entity.type === EstateType.Apartment
      ? `, ${t("estate:estate.flatNum")}.${estate.entity.content.room}`
      : "";

  const address = estate
    ? `${t("offer.saintPetersburg")}, ${estate?.address?.streetName} ${estate?.address?.index}${flatNumber}`
    : "";

  const livingCapacity = estate ? estate?.tenants?.length : 0;

  const garageCapacity = estate ? estate?.vehicles?.length : 0;

  const toggleAgree = useCallback(() => {
    setIsAgree((prev) => !prev);
  }, []);

  const handleAccept = useCallback(() => {
    resolveAccept();
  }, [resolveAccept]);

  const handleReject = useCallback(() => {
    reject();
  }, [reject]);

  useEffect(() => {
    if (!isShow) {
      setIsAgree(false);
    }
  }, [isShow]);

  const footer = (
    <div className="flex flex-col gap-2">
      <div className="grid grid-cols-2 gap-2">
        <Button
          className="h-[2.75rem]"
          onClick={handleAccept}
          disabled={pending || isLoading || !isAgree}
          loading={pending || isLoading}
        >
          {t("offer.tenant.resolver.checkIn")}
        </Button>
        <Button
          className="h-[2.75rem]"
          onClick={handleReject}
          disabled={pending || isLoading}
          loading={pending || isLoading}
          variant="solid-surface"
        >
          {t("offer.isCancel")}
        </Button>
      </div>
    </div>
  );

  const leftText = (
    <div className="flex items-center gap-2.5">
      <House3Icon size={17} />
      <p className="text-15 font-bold leading-none">
        {t("offer.tenant.info.house")} #{estate?.id}
      </p>
    </div>
  );

  const rightText = (
    <div className="flex flex-col gap-0.5 items-end ml-auto">
      <p className="text-14 font-bold">{formatPrice(estate?.baseFee)} ₽</p>
      <p className="text-11 font-medium uppercase">{t("offer.estate.info.baseFee")}</p>
    </div>
  );

  return (
    <Modal
      title={t("offer.tenant.resolver.title")}
      open={isShow}
      onClose={handleReject}
      footer={footer}
      className="w-[30rem] pt-6.5 pb-7.5 pl-7.5 pr-6.5"
      classNameCloseBtn="top-6.5 right-7.5"
      style={{ fontFamily: "TT Norms Pro" }}
    >
      <div className="flex flex-col">
        <OfferPlayerInfo
          text={t("offer.owner")}
          id={offerResolver?.baseCharacter?.id}
          name={offerResolver?.baseCharacter?.name}
          avatar={offerResolver?.baseCharacter?.avatar}
        />
        <p className="mt-4 mb-3.5 text-16 font-medium">{t("offer.tenant.resolver.residence")}:</p>
        <OfferView
          src={src}
          level={level}
          leftText={leftText}
          rightText={rightText}
          isLoading={pending}
        />
        <OfferEstateLocation address={address} />
        <OfferEstateInfo livingCapacity={livingCapacity} garageCapacity={garageCapacity} />
        <OfferCheckbox
          isChecked={isAgree}
          onChange={toggleAgree}
          label={t("offer.tenant.resolver.agree")}
          className="mt-5"
        />
      </div>
    </Modal>
  );
}
